import { CardinalDirection, IPlane } from '../api.js';
import { IBoundingSphere } from './IBoundingSphere.js';
import { ITransformedBoundingBox } from './ITransformedBoundingBox.js';
import { IVector3 } from './IVector3.js';
export class IBoundingBox {
    constructor(min, max) {
        return {
            min: IVector3.copy(min),
            max: IVector3.copy(max)
        };
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    static Invalid() {
        return {
            min: new IVector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
            max: new IVector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY)
        };
    }
    /**
     * Type guard to check if the provided object is applicable to the `IBoundingBox` interface.
     * @returns Type assertion if the object is applicable to the IBoundingBox.
     */
    static is(value) {
        const bounding_box = value;
        return 'min' in bounding_box && 'max' in bounding_box && IVector3.is(bounding_box.min) && IVector3.is(bounding_box.max);
    }
    static equals(a, b) {
        return IVector3.equals(a.min, b.min) && IVector3.equals(a.max, b.max);
    }
    static copy(bounds) {
        return new IBoundingBox(bounds.min, bounds.max);
    }
    static get_center(bounds) {
        return IVector3.lerp(bounds.min, bounds.max, 0.5);
    }
    static size(bounds) {
        if (!IBoundingBox.is_valid(bounds))
            return new IVector3(NaN, NaN, NaN);
        return new IVector3(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z);
    }
    /**
     * Calculates the union of the given bounds.
     * @param bounds
     * @returns
     */
    static union(...bounds) {
        const min = new IVector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        const max = new IVector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
        for (let i = 0; i < bounds.length; i++) {
            const bound = bounds[i];
            const effective_bound = ('transform' in bound) ? ITransformedBoundingBox.get_encapsulating_axis_aligned_bounding_box(bound) : bound;
            IVector3.min_to_ref(min, effective_bound.min, min);
            IVector3.max_to_ref(max, effective_bound.max, max);
        }
        return new IBoundingBox(min, max);
    }
    static encapsulate_in_place(bound, ...points) {
        for (const point of points) {
            IVector3.min_to_ref(bound.min, point, bound.min);
            IVector3.max_to_ref(bound.max, point, bound.max);
        }
        return bound;
    }
    /**
     * Calculates the intersection volume of the given bounds
     * @param bounds The bounds whose intersection to calculate
     * @returns The intersecting volume (a bounding box)
     */
    static intersection(...bounds) {
        if (bounds.length === 0) {
            const min = new IVector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
            const max = new IVector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
            return new IBoundingBox(min, max);
        }
        else {
            const min = IVector3.copy(bounds[0].min);
            const max = IVector3.copy(bounds[0].max);
            for (let i = 1; i < bounds.length; i++) {
                IVector3.max_to_ref(min, bounds[i].min, min);
                IVector3.min_to_ref(max, bounds[i].max, max);
            }
            return new IBoundingBox(min, max);
        }
    }
    /**
     * Calculates whether there is an intersection volume of the given bounds
     * @param bounds
     * @returns true if all bounds intersect
     */
    static intersects(...bounds) {
        return IBoundingBox.is_valid(IBoundingBox.intersection(...bounds));
    }
    /**
     * Calculates if the given bounds are valid, i.e. has a volume
     * @param bounds The bounds to check
     * @returns true if the bounds are valid
     */
    static is_valid(bounds) {
        if (bounds.max.x < bounds.min.x || bounds.max.y < bounds.min.y || bounds.max.z < bounds.min.z)
            return false;
        if (bounds.min.x === bounds.max.x && bounds.min.y === bounds.max.y && bounds.min.z === bounds.max.z)
            return false;
        return true;
    }
    /**
     * Calculates the vertices of the bounding box, optionally transformed by transform.
     * @param bounding_box The bounding box whose vertices to extract
     * @returns An array of 8 vertices describing the corners of a box.
     */
    static get_vertices(bounding_box) {
        const result = [];
        for (let i = 0; i < 8; i++) {
            result.push(IVector3.lerp(bounding_box.min, bounding_box.max, IBoundingBox._identity_vertices[i]));
        }
        return result;
    }
    static to_bounding_sphere(bounding_box) {
        return new IBoundingSphere(IVector3.lerp(bounding_box.min, bounding_box.max, 0.5), 0.5 * IVector3.distance(bounding_box.min, bounding_box.max));
    }
    static get_face_plane(bounds, direction) {
        const center = IVector3.scale(IVector3.add(bounds.min, bounds.max), 0.5);
        switch (direction) {
            case CardinalDirection.RIGHT:
                return new IPlane({ x: -1, y: 0, z: 0 }, { x: bounds.min.x, y: center.y, z: center.z });
            case CardinalDirection.LEFT:
                return new IPlane({ x: 1, y: 0, z: 0 }, { x: bounds.max.x, y: center.y, z: center.z });
            case CardinalDirection.BOTTOM:
                return new IPlane({ x: 0, y: -1, z: 0 }, { x: center.x, y: bounds.min.y, z: center.z });
            case CardinalDirection.TOP:
                return new IPlane({ x: 0, y: 1, z: 0 }, { x: center.x, y: bounds.max.y, z: center.z });
            case CardinalDirection.FRONT:
                return new IPlane({ x: 0, y: 0, z: 1 }, { x: center.x, y: center.y, z: bounds.max.z });
            case CardinalDirection.BACK:
                return new IPlane({ x: 0, y: 0, z: -1 }, { x: center.x, y: center.y, z: bounds.min.z });
            default:
                // Either we missed an enum, or the user sent in something else.
                throw new Error("Unknown cardinal direction. This shouldn't happen.");
        }
    }
}
// Helper array containing the vertices of an AABB in normalized coordinates (0 to 1)
IBoundingBox._identity_vertices = [
    new IVector3(0, 0, 0),
    new IVector3(1, 0, 0),
    new IVector3(0, 1, 0),
    new IVector3(1, 1, 0),
    new IVector3(0, 0, 1),
    new IVector3(1, 0, 1),
    new IVector3(0, 1, 1),
    new IVector3(1, 1, 1)
];
//# sourceMappingURL=IBoundingBox.js.map